import { useMutation } from '@tanstack/react-query';
import { type ReactElement, useState } from 'react';

import { Button } from '@autop/ui/src/button';
import { Field } from '@autop/ui/src/field';

import { useTRPC } from '@/shared/api';

/**
 * 인증 메일 재발송.
 *
 * 링크가 만료됐을 때 이 창구가 없으면 사용자는 막다른 길에 선다 — 서버가 주는 문구
 * 자체가 "인증 메일을 다시 받아 주세요"라서, 할 수 없는 일을 시키는 안내가 된다.
 *
 * **결과를 구분해 보여주지 않는다.** 서버가 가입 여부·인증 여부와 무관하게 같은 응답을
 * 주기 때문이다(계정 열거 방지). 여기서 "그런 계정 없음"을 표시하면 서버가 애써 감춘
 * 사실을 화면이 도로 알려주는 셈이 된다.
 */
export function ResendVerificationForm({
  defaultEmail = '',
}: {
  /**
   * 방금 로그인에 실패한 주소. 부르는 쪽이 이미 받아 둔 값이 있으면 채워 준다 —
   * 같은 화면에서 같은 주소를 두 번 적게 하지 않는다. 처음 값만 쓰고 그 뒤에는
   * 사용자가 고칠 수 있게 둔다(다른 계정의 메일을 다시 받을 수도 있다).
   */
  defaultEmail?: string;
} = {}): ReactElement {
  const trpc = useTRPC();
  const [email, setEmail] = useState(defaultEmail);
  const resend = useMutation(trpc.auth.resendVerification.mutationOptions());

  if (resend.isSuccess) {
    return (
      // 폼이 조용히 안내로 바뀐다 — 알려 주지 않으면 화면을 못 보는 사용자는
      //   메일이 나갔는지 알 방법이 없다.
      <div
        role="status"
        className="flex flex-col gap-2 rounded-lg border border-border p-6 text-center"
      >
        <p className="text-[15px] font-semibold">메일함을 확인해 주세요</p>
        <p className="text-[13px] text-muted-foreground">
          가입된 주소라면 인증 메일을 다시 보냈습니다.
          <br />
          메일이 오지 않았다면 스팸함도 확인해 주세요.
        </p>
      </div>
    );
  }

  return (
    <form
      // 이 폼은 로그인 폼 아래에 함께 놓일 수 있다. 이름이 없으면 화면 낭독기에서
      //   두 폼이 구분되지 않고, 「이메일」 입력도 어느 쪽 것인지 알 수 없다.
      aria-label="인증 메일 다시 받기"
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        resend.mutate({ email });
      }}
    >
      <Field
        id="resend-email"
        type="email"
        label="이메일"
        placeholder="name@example.com"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <Button type="submit" size="lg" className="w-full" disabled={resend.isPending}>
        인증 메일 다시 받기
      </Button>
      {resend.error === null ? null : (
        <p className="text-[12.5px] text-destructive">{resend.error.message}</p>
      )}
    </form>
  );
}
